import { t } from '@lingui/core/macro';
import { useMemo } from 'react';

import { useIntegrationProvidersByCapability } from '@/api/integration';
import EventIcon from '@/assets/svg/bx-bolt.svg?react';
import CompanyIcon from '@/assets/svg/bx-buildings.svg?react';

import type { RuleEntityAttributeType, RuleEntityType } from './types';

const useRuleEntities = () => {
  const { providers, isLoading, error } = useIntegrationProvidersByCapability('intent');
  const hasIntentProvidersConnected = (providers?.length ?? 0) > 0;

  const ruleEntities = useMemo(() => {
    const companyAttributes: RuleEntityAttributeType[] = [
      {
        id: 'name',
        type: 'string',
        display: 'autocomplete',
        name: t`Name`,
        icon: <CompanyIcon />,
      },
      {
        id: 'industry',
        type: 'string',
        display: 'picklist',
        name: t`Industry`,
        icon: <CompanyIcon />,
      },
      {
        id: 'revRange',
        type: 'string',
        display: 'picklist',
        name: t`Revenue Size`,
        icon: <CompanyIcon />,
      },
      {
        id: 'region',
        type: 'string',
        display: 'picklist',
        name: t`Region`,
        icon: <CompanyIcon />,
      },
      {
        id: 'audience',
        type: 'string',
        display: 'picklist',
        name: t`Audience`,
        icon: <CompanyIcon />,
      },
    ];

    const ruleEntities: RuleEntityType[] = [
      {
        id: 'company',
        name: t`Company Attribute`,
        icon: <CompanyIcon />,
        cardinality: 'one',
        attributes: companyAttributes,
      },
      {
        id: 'event.visit',
        name: t`Visited website`,
        icon: <EventIcon />,
        cardinality: 'many',
        hideAggregate: false,
        attributes: [
          {
            id: 'url',
            type: 'string',
            display: 'text',
            name: t`URL`,
          },
          {
            id: 'channel',
            type: 'string',
            display: 'picklist',
            name: t`Channel`,
          },
          {
            id: 'vendor',
            type: 'string',
            display: 'picklist',
            name: t`Vendor`,
          },
          {
            id: 'campaign',
            type: 'string',
            display: 'autocomplete',
            name: t`Campaign`,
          },
          {
            id: 'adGroup',
            type: 'string',
            display: 'autocomplete',
            name: t`Ad Group`,
          },
          {
            id: 'ad',
            type: 'string',
            display: 'autocomplete',
            name: t`Keyword`,
          },
        ],
      },
      {
        id: 'event.impression',
        name: t`Viewed impression`,
        icon: <EventIcon />,
        cardinality: 'many',
        hideAggregate: false,
        attributes: [
          {
            id: 'pixel',
            type: 'string',
            display: 'autocomplete',
            name: t`Pixel`,
          },
        ],
      },
      {
        id: 'event.intent',
        name: t`Had intent signal`,
        icon: <EventIcon />,
        cardinality: 'many',
        hidden: !hasIntentProvidersConnected,
        hideAggregate: true,
        attributes: [
          {
            id: 'provider',
            type: 'string',
            display: 'picker',
            name: t`Intent Provider`,
            isDefault: true,
            isRequired: true,
          },
          {
            id: 'signalType',
            type: 'string',
            display: 'picklist',
            name: t`Signal Type`,
          },
          {
            id: 'productCategory',
            type: 'string',
            display: 'autocomplete',
            name: t`Product/Category`,
          },
        ],
      },
      {
        id: 'event.impactscore',
        name: t`Impact Score`,
        icon: <EventIcon />,
        cardinality: 'many',
        hideAggregate: false,
        aggregateFunction: 'sum',
        attributes: [],
      },
    ];

    return ruleEntities;
  }, [hasIntentProvidersConnected]);

  return { ruleEntities, isLoading, error };
};

export default useRuleEntities;
